'use client';

import { useTranslations, useLocale } from 'next-intl';
import { StarIcon } from 'lucide-react';
import { useMemo, useEffect } from 'react';
import { notFound } from 'next/navigation';
import { getProjectById, getAllProjects } from '@/data/projects';
import BottomNavigation from "@/components/layout/BottomNavigation";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/routing";

interface ProjectDetailsContentProps {
  projectId: string;
}

export default function ProjectDetailsContent({ projectId }: ProjectDetailsContentProps) {
  const t = useTranslations('ProjectDetails');
  const tProjects = useTranslations('ProjectDetails.projects');
  
  const project = useMemo(() => {
    return getProjectById(projectId);
  }, [projectId]);

  // Get translated project data
  const translatedProject = useMemo(() => {
    try {
      // Access the project data from translations
      const projectData = tProjects.raw(projectId);
      if (projectData && typeof projectData === 'object') {
        return projectData as any;
      }
    } catch (e) {
      console.log('Translation not found for project:', projectId, 'Error:', e);
    }
    return null;
  }, [tProjects, projectId]);

  // Get all projects and find current index
  const allProjects = useMemo(() => getAllProjects(), []);
  const currentIndex = useMemo(() => {
    return allProjects.findIndex(p => p.id === projectId);
  }, [allProjects, projectId]);

  // Get previous and next projects
  const previousProject = currentIndex > 0 ? allProjects[currentIndex - 1] : null;
  const nextProject = currentIndex < allProjects.length - 1 ? allProjects[currentIndex + 1] : null;

  // Redirect to 404 if project doesn't exist
  useEffect(() => {
    if (!project) {
      notFound();
    }
  }, [project]);

  // Don't render anything if project doesn't exist (will redirect)
  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Project Image - Full Width on Mobile, Container on Desktop */}
      <div className="lg:container lg:mx-auto lg:px-4 xl:px-8">
        <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden lg:rounded-b-xl">
          <img
            src={project.image}
            alt={translatedProject?.title || project.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Main Content - With Container */}
      <div className="container mx-auto py-12 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-12">
            
            {/* Project Overview Section */}
            <section>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('section1.title')}
              </h1>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  {t('section1.description1')}
          </p>
        </div>
        
              {/* Technologies List */}
              <div className="mt-8 space-y-3">
                {[t('section1.feature1'), t('section1.feature2'), t('section1.feature3')].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                      <circle cx="10" cy="10" r="10" fill="url(#paint0_linear_first)"/>
                      <path d="M14.5 7L8.5 13L5.5 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <defs>
                        <linearGradient id="paint0_linear_first" x1="-0.126677" y1="7.18962" x2="20.2194" y2="7.54811" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#351EE0"/>
                          <stop offset="1" stopColor="#3F74EF"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="text-gray-900 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 text-gray-600 leading-relaxed">
                <p>{project.details?.problem || t('section1.description2')}</p>
              </div>
            </section>

            {/* Project Solution Section with Image */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('section2.title')}
              </h2>
              
              <div className="space-y-4 text-gray-600 leading-relaxed mb-6">
                <p>
                  {project.details?.solution || t('section2.description1')}
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start mb-6">
                {/* Fixed Rectangle Image - Left Side */}
                <div className="lg:col-span-1">
                  <div className="w-full h-[184px] rounded-lg overflow-hidden">
                    <img
                      src={project.secondaryImage || project.image}
                      alt="Project Solution"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                {/* Features List - Right Side */}
                <div className="lg:col-span-2">
                  <div className="space-y-3">
                    {[t('section2.feature1'), t('section2.feature2'), t('section2.feature3')].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <svg width="26" height="27" viewBox="0 0 26 27" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                          <path d="M12.3398 17.5156C11.7812 18.0742 10.918 18.0742 10.3594 17.5156L7.10938 14.2656C6.55078 13.707 6.55078 12.8438 7.10938 12.2852C7.66797 11.7266 8.53125 11.7266 9.08984 12.2852L11.375 14.5195L16.8594 9.03516C17.418 8.47656 18.2812 8.47656 18.8398 9.03516C19.3984 9.59375 19.3984 10.457 18.8398 11.0156L12.3398 17.5156ZM26 13.25C26 20.4609 20.1602 26.25 13 26.25C5.78906 26.25 0 20.4609 0 13.25C0 6.08984 5.78906 0.25 13 0.25C20.1602 0.25 26 6.08984 26 13.25ZM13 2.6875C7.16016 2.6875 2.4375 7.46094 2.4375 13.25C2.4375 19.0898 7.16016 23.8125 13 23.8125C18.7891 23.8125 23.5625 19.0898 23.5625 13.25C23.5625 7.46094 18.7891 2.6875 13 2.6875Z" fill="url(#paint0_linear_second)"/>
                          <defs>
                            <linearGradient id="paint0_linear_second" x1="-0.164679" y1="9.34651" x2="26.2852" y2="9.81254" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#351EE0"/>
                              <stop offset="1" stopColor="#3F74EF"/>
                            </linearGradient>
                          </defs>
                        </svg>
                        <span className="text-blue-600 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Project Details Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden sticky top-8">
              
              {/* Card Header */}
              <div className="bg-brand-gradient px-6 py-4">
                <h3 className="text-xl font-bold text-white text-center">
                  {t('projectDetails.title')}
                </h3>
              </div>
              
              {/* Card Body */}
              <div className="p-6 space-y-4">
                <div>
                  <span className="text-gray-500 text-sm">{t('projectDetails.name')}</span>
                  <p className="text-gray-900 font-medium">{translatedProject?.title || project.title}</p>
                </div>
                
                <div>
                  <span className="text-gray-500 text-sm">{t('projectDetails.author')}</span>
                  <p className="text-gray-900 font-medium">{project.author}</p>
                </div>
                
                <div>
                  <span className="text-gray-500 text-sm">{t('projectDetails.date')}</span>
                  <p className="text-gray-900 font-medium">{project.date}</p>
                </div>
                
                <div>
                  <span className="text-gray-500 text-sm">{t('projectDetails.tags')}</span>
                  <p className="text-gray-900 font-medium">{project.tags}</p>
                </div>
                
                <div>
                  <span className="text-gray-500 text-sm">{t('projectDetails.value')}</span>
                  <p className="text-gray-900 font-medium">{project.value}</p>
                </div>
                
                {/* Rating */}
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`w-5 h-5 ${
                        star <= project.rating 
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button
          asChild
          className="w-full h-auto mt-10 inline-flex items-center justify-center gap-2.5 px-6 md:px-8 lg:px-[30px] py-3 md:py-4 lg:py-5 rounded-[5px] bg-brand-gradient text-white font-button font-[number:var(--button-font-weight)] text-sm md:text-base lg:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] hover:opacity-90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <Link
            href={{
              pathname: '/feedback',
              query: { projectId: project.id }
            }}
          >
            {t('feedbackCTA')} 
          </Link>
        </Button>
      </div>

      <BottomNavigation
        previous={
          previousProject
            ? {
                href: `/projects/${previousProject.id}`,
                label: previousProject.title
              }
            : null
        }
        next={
          nextProject
            ? {
                href: `/projects/${nextProject.id}`,
                label: nextProject.title
              }
            : null
        }
      />
    </div>
  );
}